<template>
  <div class="flex flex-col space-y-2.5 pa-5">
    <div class="flex justify-between" style="width: 100%;">
      <div>
        <div class="flex">
          <v-btn color="primary" v-if="authPermissions.has('inventory_monitoring:recalculate')" dense
            @click="recalculate">Recalculate</v-btn>
          <div>
            <v-text-field outlined dense placeholder="Search..." append-icon="mdi-magnify"
              v-model="search"></v-text-field>
          </div>
        </div>
      </div>
      <div class="space-y-2">
        <div class="flex space-x-2.5">
          <v-btn color="primary" :loading="fetching" @click="exportExcel">Excel</v-btn>
          <v-btn color="primary" :loading="fetching" @click="exportPdf">PDF</v-btn>
        </div>
        <v-autocomplete v-model="visibleColumns" :items="headerColumnList" item-text="text" item-value="value"
          label="Visible Columns" multiple clearable dense outlined chips style="min-width: 300px">
          <!-- Custom rendering for each item in dropdown -->
          <template #item="{ item, on, attrs }">
            <v-list-item v-bind="attrs" v-on="on">
              <v-list-item-content>
                <v-list-item-title>{{ item.text }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>

          <!-- Custom rendering for selected items as chips -->
          <template #selection="{ item, index }">
            <v-chip v-if="item" :key="index" class="ma-1" small outlined>
              {{ item.text }}
            </v-chip>
          </template>
        </v-autocomplete>
      </div>
    </div>
    <div>
      <v-data-table :page.sync="page" :search="search" :loading="fetching" :items="inventoryItems" :headers="headers"
        dense :custom-filter="customFilter">
        <template #body.prepend="{ }">
          <td style="border-bottom: 1px solid #E0E0E0;" class="text-left " colspan="4">Total Worth</td>
          <td style="border-bottom: 1px solid #E0E0E0;" class="text-right">{{ formatAccounting(inventoryTotalWorth) }}
          </td>
          <td style="border-bottom: 1px solid #E0E0E0;" :colspan="headers.length - 5 < 0 ? 0 : headers.length - 5"></td>
        </template>
        <template #item.srp="{ item }">
          {{ formatAccounting(item.stock.retail) }}
        </template>
        <template #item.worth="{ item }">
          <span :class="item.stock.retail * (item.quantity || 0) < 0 ? 'red--text' : ''">
            {{ formatAccounting(item.stock.retail * (item.quantity || 0)) }}
          </span>
        </template>
        <template #item.unit="{ item }">
          {{ item.stock.unit }}
        </template>
        <template #item.stock="{ item }">
          <div>
            {{ item.stock.product_name }}
          </div>
        </template>
        <template #item.quantity="{ value }">
          <div>
            <span v-if="!value" class="red--text line-through">
              {{ value }}
            </span>
            <span v-else-if="value < 0" class="red--text">
              ({{ Math.abs(value) }})
            </span>
            <span v-else class="green--text">{{ value }}</span>
          </div>
        </template>
        <template #item.action="{ item }">
          <div class="flex">
            <div v-if="authPermissions.has('inventory_monitoring:recalculate')">
              <v-icon color="purple" @click="recalculateInventory(item)">mdi-calculator</v-icon>
            </div>
            <div v-if="authPermissions.has('inventory_monitoring:recalculate')">
              <v-tooltip bottom color="primary">
                <template #activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" color="primary"
                    @click="getInventoryBreakdown(item.stock_id)">mdi-table</v-icon>
                </template>
                View Inventory Breakdown
              </v-tooltip>
            </div>
            <div v-if="authPermissions.has('inventory_monitoring_restore_point:create')">
              <v-icon v-if="!item.creatingRestorePoint" color="green"
                @click="createRestorePoint(item)">mdi-refresh</v-icon>
              <v-progress-circular v-else :width="2" :size="15" indeterminate color="green"></v-progress-circular>
            </div>
            <v-icon v-if="authPermissions.has('inventory_monitoring:set')" color="yellow darken-3"
              @click="$emit('edit', item.id)">mdi-pencil</v-icon>
          </div>
        </template>
      </v-data-table>
    </div>
    <v-dialog v-model="dialog">
      <v-card>
        <v-toolbar color="primary" dark>
          <span>
            Quantity Remaining Breakdown (Recalculate)
          </span>
          <v-spacer></v-spacer>
          <v-icon @click="dialog = false">mdi-close</v-icon>
        </v-toolbar>
        <v-container>
          <v-simple-table>
            <template #default>
              <thead>
                <tr>
                  <th>Group</th>
                  <th>Receipt No.</th>
                  <th>Qty</th>
                  <th>Qty Remain</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item) in inventoryBreakdownItems" :key="item.index">
                  <td>{{ item.group_label }}</td>
                  <td>{{ item.receipt_no }}</td>
                  <td>
                    <span v-if="item.operation === '+'" class="success--text">{{ item.quantity }}</span>
                    <span v-else-if="item.operation === '-'" class="error--text">{{ item.quantity }}</span>
                    <span v-else>{{ item.quantity }}</span>
                    ({{ item.quantity_unit }})
                  </td>
                  <td>{{ item.quantity_remaining }} ({{ item.quantity_remaining_unit }})</td>
                  <td>{{ item.timestamp }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-container>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import axios from '@/extra-functions/axios.extra'
import { mapState } from 'vuex'
import { downloadCSV, ErrorPopup, formatAccounting, LoadingPopup, SuccessPopup, useWatchEffect } from '../../../../lib/utils'
import StoreInventoryPdf from '../../../../pdf/store-inventory.pdf'
import appAxios from '../../../../extra-functions/axios.extra'
import { debugLog } from '@/lib/electron-utils.js'

const [watchEffect, clearWatchEffect] = useWatchEffect()

export default {
  name: 'InventoryTable',
  data() {
    return {
      recalculating: false,
      fetching: false,
      inventoryItems: [],
      search: '',
      page: 1,
      headerFilter: [],
      visibleColumns: [],
      inventoryBreakdownItems: [],
      dialog: false,
    }
  },
  created() {
    this.getIndex()
    this.visibleColumns = this.headerColumnList.map(col => col.value) // show all by default
  },
  destroyed() {
    clearWatchEffect()
  },
  computed: {
    ...mapState({
      authToken: state => state.auth.token,
      currentStoreId: state => state.settings.storeId,
      otherStores: state => state.branchLocation.list.filter(item => item.id !== state.settings.storeId),
      branchLocations: state => state.branchLocation.list.reduce((acc, item) => {
        acc[item.id] = item.name
        return acc
      }, {}),
      authPermissions: state => state.auth.userPermissions,
    }),
    headerColumnList() {
      const otherStores = this.otherStores.map(store => ({
        text: store.name,
        value: `store_${store.id}`,
      }))
      const columns = [
        { text: 'Branch', value: 'store.name', width: 'wrap' },
        {
          text: 'Code',
          value: 'stock.stock_code',
          sortable: true,
        },
        {
          text: 'Item',
          value: 'stock',
        },
        {
          text: 'SRP',
          value: 'srp',
          align: 'right',
        },
        {
          text: 'Worth',
          value: 'worth',
          align: 'right',
        },
        {
          text: 'Unit',
          value: 'unit',
          align: 'center',
        },
        ...otherStores,
        { text: 'Qty', value: 'quantity', align: 'center', sortable: true },
        { text: 'Action', value: 'action', align: 'center' },
      ]
      return columns
    },
    headers() {
      const allColumns = [...this.headerColumnList]
      if (!this.visibleColumns || this.visibleColumns.length === 0) {
        return allColumns // fallback to all if none selected
      }
      const visibleCols = new Set([...this.visibleColumns])
      debugLog('visibleCols', [...visibleCols])
      return allColumns.filter(col => visibleCols.has(col.value))
    },
    inventoryTotalWorth() {
      return this.inventoryItems.reduce((prev, next) => prev + (next.stock.retail * (next.quantity || 0)), 0)
    },
  },
  methods: {
    async recalculate() {
      try {
        if (this.recalculating) return
        LoadingPopup.fire({ text: 'Recalculating inventory, please wait...' })
        this.recalculating = true
        await axios(this.authToken).post('/inventories-v2/recalculate')
        const data = await this.getIndex()
        this.inventoryItems = data
        SuccessPopup.fire({ text: 'Recalculation completed successfully!' })
        return data
      } catch (e) {
        console.error(e.message)
        ErrorPopup.fire()
        // this.$alertSwalError('oops something went wrong!')
      } finally {
        LoadingPopup.close()
        this.recalculating = false
      }
    },

    /**
     * Description
     * @param {AbortController} [controller]
     */
    async getIndex(controller) {
      try {
        this.fetching = true
        const { data } = await axios(this.authToken).get('/inventories-v2',
          { signal: controller?.signal },
        )
        this.inventoryItems = data
        return [...data]
      } catch (e) {
        console.error(e.message)
        this.$alertSwalError('oops something went wrong!')
      } finally {
        this.fetching = false
      }
    },
    customFilter(_, search, item) {
      if (!search) return true
      const searchRegEx = new RegExp(search, 'i')
      const isProductPass = searchRegEx.test(item?.stock?.product_name)
      if (isProductPass) return isProductPass
      const isUnitPass = searchRegEx.test(item?.stock?.unit)
      if (isUnitPass) return isUnitPass
      const isStockCodePass = searchRegEx.test(item?.stock?.stock_code)
      if (isStockCodePass) return isStockCodePass
      return false
    },
    async recalculateInventory(item) {
      try {
        if (!item.stock_id) throw new Error('item.stock_id is undefined')
        if (!item.stock) throw new Error('stock is undefined')
        const confirm = await this.$confirm(`are you sure you want to recalculate inventory for ${item.stock.product_name}?`)
        if (!confirm) return
        LoadingPopup.fire({ text: 'Recalculating Inventory...' })
        item.recalculating = true
        this.inventoryItems = [...this.inventoryItems]
        const { data } = await axios(this.authToken).post(`/stocks/${item.stock_id}/recalculate-inventory`)
        this.inventoryItems = data
        SuccessPopup.fire({ text: `successfully recalculated inventory for ${item.stock.product_name}` })
      } catch (e) {
        console.error(e.message)
        ErrorPopup.fire()
      } finally {
        item.recalculating = false
        this.inventoryItems = [...this.inventoryItems]
      }
    },
    async createRestorePoint(item) {
      try {
        if (!item.stock_id) throw new Error('item.stock_id is undefined')
        if (!item.stock) throw new Error('stock is undefined')
        const confirm = await this.$confirm(`are you sure you want to recalculate inventory for ${item.stock.product_name}?`)
        if (!confirm) return
        item.creatingRestorePoint = true
        this.inventoryItems = [...this.inventoryItems]
        await axios(this.authToken).post(`/stocks/${item.stock_id}/restore-point`)
        this.$alertSwalSuccess(`successfully created a inventory restore point for ${item.stock.product_name}`)
      } catch (e) {
        console.error(e.message)
        this.$alertSwalError('oops something went wrong')
      } finally {
        item.creatingRestorePoint = false
        this.inventoryItems = [...this.inventoryItems]
      }
    },
    exportExcel() {
      watchEffect(async (onCleanup) => {
        const controller = new AbortController()
        onCleanup(() => {
          controller.abort()
        })
        const data = await this.getIndex(controller)
        const headers = [
          'store',
          'stock code',
          'srp',
          'category',
          'description',
          'brand name',
          'unit',
          'quantity',
        ]
        const rows = data.map(item => [
          item?.store?.name,
          item?.stock?.stock_code,
          item?.stock?.retail,
          item?.stock?.category,
          item?.stock?.description || '',
          item?.stock?.brand_name,
          item?.stock?.unit,
          item?.quantity,
        ])
        const storeName = data[0]?.store?.name
          ?.replace?.(' ', '_')
          ?.toLowerCase?.()
        const csvFilename = `${storeName}_inventory_excel_${(new Date()).getTime()}.csv`
        downloadCSV([headers, ...rows], csvFilename)
      }, 'exportGetIndex')
    },
    exportPdf() {
      watchEffect(async (onCleanup) => {
        const controller = new AbortController()
        onCleanup(() => {
          controller.abort()
        })
        const data = await this.getIndex(controller)
        const rows = data.map(item => {
          if (!item.quantity) {
            const textColor = [179, 11, 0]
            return [
              { content: item?.stock?.stock_code, styles: { textColor } },
              { content: item?.stock?.description || '', styles: { textColor } },
              { content: item?.stock?.brand_name, styles: { textColor } },
              { content: item?.stock?.unit, styles: { textColor } },
              { content: item?.quantity, styles: { textColor } },
            ]
          }
          return [
            item?.stock?.stock_code,
            item?.stock?.description || '',
            item?.stock?.brand_name,
            item?.stock?.unit,
            item?.quantity,
          ]
        })
        const pdf = new StoreInventoryPdf()
        pdf.generate(rows)
      }, 'exportGetIndex')
    },
    async getInventoryBreakdown(stockId) {
      try {
        LoadingPopup.fire({
          text: 'Please wait while I retrieve the inventory breakdown...',
        })
        const { data } = await appAxios(this.authToken).get(`/inventory-breakdown/${stockId}`)
        this.inventoryBreakdownItems = data
        LoadingPopup.close()
        this.dialog = true
      } catch (e) {
        console.error(e)
        ErrorPopup.fire()
      }
    },
    formatAccounting,
  },
}
</script>
